import _ from 'lodash';
import { useMemo } from 'react';
import { useMutation } from '@apollo/client';

import {
  ASSESSMENT_SESSION_LIST_FIELDS,
  ASSIGN_JERSEY,
  CLOSE_ASSESSMENT_SESSION,
  COUNT_ARCHIVED_ASSESSMENT_SESSIONS,
  COUNT_CLOSED_ASSESSMENT_SESSIONS,
  CREATE_ASSESSMENT_SESSIONS,
  OPEN_ASSESSMENT_SESSION,
  REMOVE_CHECKED_IN_PLAYER,
  UNASSIGN_JERSEY,
  SESSION_SCHEDULE_REFETCH_QUERIES,
} from '../queries/assessment-session.queries';
import { getAuthUser } from './auth.service';
import { useActiveAgeGroupIdsInAssessment } from './age-group.service';
import { useRetryKey } from '../hooks/use-retry-key';
import { addRefCacheModifier, createCacheModifier, removeCacheModifier } from '../cache/basic.cache';
import { REMOVE_ASSESSMENT_SESSION, RESTORE_ASSESSMENT_SESSION } from '../queries/assessment-session.queries';
import { nanoid } from 'nanoid';
import { GET_PLAYER_LIST } from '../../components/Player/PlayerList/player-list.graphql';

// Server-side counts can't be recomputed client-side; read-then-write so each of
// N bulk mutations shifts the cached count instead of all writing the same value.
const shiftCachedCount = (cache: any, query: any, field: string, variables: any, delta: number) => {
  const current = cache.readQuery({ query, variables });

  // An uncached count is unknown, not zero: a delta written over it renders a wrong total
  // until the in-flight query resolves.
  if (!current || !_.isNumber(current[field])) return;

  cache.writeQuery({ query, variables, data: { [field]: Math.max((current[field] as number) + delta, 0) } });
};

// The session lists' variables: the active assessment and its selected age groups. The list queries
// and the mutations that shift their cached counts share this, so both address the same cache entry.
export const useAssessmentSessionListScope = () => {
  const user = getAuthUser();
  const { ageGroupIds, loading, error } = useActiveAgeGroupIdsInAssessment();
  const assessmentId: string | undefined = user?.settings.activeAssessmentId;

  const variables = useMemo(() => ({ assessmentId, ageGroupIds }), [assessmentId, ageGroupIds]);

  return { variables, loading, error };
};

// Refetched only once the age groups are known — the player list query requires them.
const playerListRefetch = (variables: { assessmentId?: string; ageGroupIds?: string[] }) =>
  variables.ageGroupIds ? [{ query: GET_PLAYER_LIST, variables }] : [];

export const buildCreateAssessmentSessions = () => {
  const user = getAuthUser();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(CREATE_ASSESSMENT_SESSIONS);

  return {
    ...rest,
    // No optimisticResponse: the rows carry no server-assigned _id, so the
    // optimistic write landed on `AssessmentSession:undefined` and blanked the list.
    createAssessmentSessions: (rows: IAssessmentSession[]) => {
      return mutation({
        variables: { rows, assessmentId: user?.settings.activeAssessmentId },
        refetchQueries: SESSION_SCHEDULE_REFETCH_QUERIES,
        update: (cache, { data }) => {
          if (data) {
            const createdAssessmentSessions = data.createAssessmentSessions;

            cache.modify({
              fields: {
                assessmentSessions: createCacheModifier({
                  cache,
                  createdDoc: createdAssessmentSessions,
                  fragment: ASSESSMENT_SESSION_LIST_FIELDS,
                  fragmentName: 'AssessmentSessionListFields',
                  modelName: 'AssessmentSession'
                })
              }
            })
          }
        }
      })
    }
  }
};

export const buildRemoveAssessmentSession = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { variables } = useAssessmentSessionListScope();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(REMOVE_ASSESSMENT_SESSION);
  // A retry of the same delete reuses its operationId, so the cancellation goes out once.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const withRetryKey = useRetryKey();

  return {
    ...rest,
    // removeAssessmentSession returns a DeleteResponse, not the session — move the
    // already-cached entity rather than writing the payload over it.
    removeAssessmentSession: (_id: string, notify: boolean) => withRetryKey({ _id, notify }, (operationId) =>
      mutation({
        variables: { _id, notify, operationId },
        update: (cache, { data }) => {
          if (data) {
            shiftCachedCount(cache, COUNT_ARCHIVED_ASSESSMENT_SESSIONS, 'countArchivedAssessmentSessions', variables, 1);

            cache.modify({
              fields: {
                assessmentSessions: removeCacheModifier(cache, _id),
                archivedAssessmentSessions: addRefCacheModifier('AssessmentSession', _id)
              }
            });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          removeAssessmentSession: {
            __typename: 'DeleteResponse',
            _id
          }
        },
        refetchQueries: [...playerListRefetch(variables), ...SESSION_SCHEDULE_REFETCH_QUERIES]
      }))
  };
};

export const buildRestoreAssessmentSession = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { variables } = useAssessmentSessionListScope();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(RESTORE_ASSESSMENT_SESSION);

  return {
    ...rest,
    restoreAssessmentSession: (_id: string) =>
      mutation({
        variables: { _id },
        update: (cache, { data }) => {
          if (data) {
            shiftCachedCount(cache, COUNT_ARCHIVED_ASSESSMENT_SESSIONS, 'countArchivedAssessmentSessions', variables, -1);

            cache.modify({
              fields: {
                archivedAssessmentSessions: removeCacheModifier(cache, _id),
                assessmentSessions: createCacheModifier({
                  cache,
                  createdDoc: [data.restoreAssessmentSession],
                  fragment: ASSESSMENT_SESSION_LIST_FIELDS,
                  fragmentName: 'AssessmentSessionListFields',
                  modelName: 'AssessmentSession'
                })
              }
            });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          restoreAssessmentSession: {
            __typename: 'AssessmentSession',
            _id
          }
        },
        refetchQueries: [...playerListRefetch(variables), ...SESSION_SCHEDULE_REFETCH_QUERIES]
      })
  };
};

export const buildCloseAssessmentSession = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { variables } = useAssessmentSessionListScope();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(CLOSE_ASSESSMENT_SESSION);

  return {
    ...rest,
    closeAssessmentSession: (_id: string) =>
      mutation({
        variables: { _id },
        update: (cache, { data }) => {
          if (data) {
            shiftCachedCount(cache, COUNT_CLOSED_ASSESSMENT_SESSIONS, 'countClosedAssessmentSessions', variables, 1);

            cache.modify({
              fields: {
                assessmentSessions: removeCacheModifier(cache, _id),
                closedAssessmentSessions: createCacheModifier({
                  cache,
                  createdDoc: [data.closeAssessmentSession],
                  fragment: ASSESSMENT_SESSION_LIST_FIELDS,
                  fragmentName: 'AssessmentSessionListFields',
                  modelName: 'AssessmentSession'
                })
              }
            });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          closeAssessmentSession: {
            __typename: 'AssessmentSession',
            _id
          }
        },
        refetchQueries: playerListRefetch(variables)
      })
  };
};

export const buildOpenAssessmentSession = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { variables } = useAssessmentSessionListScope();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(OPEN_ASSESSMENT_SESSION);

  return {
    ...rest,
    openAssessmentSession: (_id: string) =>
      mutation({
        variables: { _id },
        update: (cache, { data }) => {
          if (data) {
            shiftCachedCount(cache, COUNT_CLOSED_ASSESSMENT_SESSIONS, 'countClosedAssessmentSessions', variables, -1);

            cache.modify({
              fields: {
                closedAssessmentSessions: removeCacheModifier(cache, _id),
                assessmentSessions: createCacheModifier({
                  cache,
                  createdDoc: [data.openAssessmentSession],
                  fragment: ASSESSMENT_SESSION_LIST_FIELDS,
                  fragmentName: 'AssessmentSessionListFields',
                  modelName: 'AssessmentSession'
                })
              }
            });
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          openAssessmentSession: {
            __typename: 'AssessmentSession',
            _id
          }
        },
        refetchQueries: playerListRefetch(variables)
      })
  };
};

export const buildAssignJersey = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(ASSIGN_JERSEY, {
    context: {
      serializationKey: 'MUTATION',
      tracked: true,
    },
  });

  return {
    ...rest,
    assignJersey: (_id: string, playerAssessmentId: string, jersey: IJersey) =>
      mutation({
        variables: { _id, playerAssessmentId, jersey },
        update: cache => {
          cache.modify({
            id: `AssessmentSession:${_id}`,
            fields: {
              checkedInPlayerIds: _ids => [..._ids, playerAssessmentId]
            }
          });
        }
      })
  };
};

export const buildRemoveCheckedInPlayer = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(REMOVE_CHECKED_IN_PLAYER, {
    context: {
      serializationKey: 'MUTATION',
      tracked: true,
    }
  });

  return {
    ...rest,
    removeCheckedInPlayer: (_id: string, playerAssessmentId: string) => {
      return mutation({
        variables: { _id, playerAssessmentId },
        update: (cache, { data }) => {
          if (data) {
            cache.modify({
              id: `AssessmentSession:${_id}`,
              fields: {
                checkedInPlayerIds: _ids => _.without(_ids, playerAssessmentId)
              }
            })
          }
        },
        optimisticResponse: {
          removeCheckedInPlayer: {
            _id: nanoid(),
            checkedInPlayers: [{ _id }]
          }
        }
      })
    }
  }
};

export const buildUnassignJersey = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(UNASSIGN_JERSEY, {
    context: {
      serializationKey: 'MUTATION',
      tracked: true,
    },
  });

  return {
    ...rest,
    unassignJersey: (_id: string, playerAssessmentId: string) =>
      mutation({
        variables: { _id, playerAssessmentId },
        update: (cache, { data }) => {
          if (data) {
            cache.modify({
              id: `AssessmentSession:${_id}`,
              fields: {
                jerseys: () => data.unassignJersey.jerseys
              }
            });

            // Update the PlayerAssessment cache
            cache.modify({
              id: `PlayerAssessment:${playerAssessmentId}`,
              fields: {
                jerseyColour: () => undefined,
                jerseyNumber: () => undefined,
              }
            });
          }
        }
      })
  };
};
